import React from "react";
import {
  Link as BaseRouterLink,
  LinkProps as BaseRouterLinkProps,
} from "react-router-dom";
import styled, { css } from "styled-components";

interface StyleProps {
  $size: "lg" | "md" | "sm";
  $color: string;
  $reverse?: boolean;
}

const linkStyles = css<StyleProps>`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${(props) => (props.$size === "sm" ? 6 : 8)}px;
  width: fit-content;
  max-width: 100%;
  padding: 0;

  cursor: pointer;
  appearance: none;
  border: 0;
  background: none;
  color: ${(props) => props.theme.colors[props.$color] || props.$color};

  font-family: ${(props) => props.theme.fonts.body};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  font-size: ${(props) => props.theme.fontSizesUI[props.$size]}px;
  text-decoration: underline;

  transition: all ${(props) => props.theme.transitions.simple};

  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:visited {
    color: ${(props) => props.theme.colors[props.$color] || props.$color};
  }

  @media (hover: hover) {
    &:hover:not([disabled]) {
      color: ${(props) =>
        props.$reverse
          ? props.theme.colors.textReverse
          : props.theme.colors.tertiary};
    }
  }
`;

const StyledLink = styled.a<StyleProps>`
  ${linkStyles};
`;

const StyledRouterLink = styled(BaseRouterLink)<StyleProps>`
  ${linkStyles};
`;

interface BaseProps {
  as?: React.ElementType;
  size?: "md" | "sm" | "lg";
  color?: string;
  reverse?: boolean;
}

interface Props
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    BaseProps {}

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    BaseProps {}

interface RouterLinkProps extends BaseRouterLinkProps, BaseProps {}

const Link: React.FC<Props> = ({
  children,
  size = "md",
  color = "primary",
  reverse,
  ...props
}) => (
  <StyledLink
    $size={size}
    $reverse={reverse}
    data-turbo="false"
    $color={color}
    {...props}
  >
    {children}
  </StyledLink>
);

export const LinkButton: React.FC<ButtonProps> = ({
  children,
  size = "md",
  disabled,
  type = "button",
  color = "primary",
  reverse,
  ...props
}) => (
  <StyledLink
    as="button"
    $size={size}
    disabled={disabled}
    type={type}
    $color={color}
    $reverse={reverse}
    {...props}
  >
    {children}
  </StyledLink>
);

export const RouterLink: React.FC<RouterLinkProps> = ({
  children,
  size = "md",
  color = "primary",
  reverse,
  ...props
}) => (
  <StyledRouterLink
    $size={size}
    data-turbo="false"
    $color={color}
    $reverse={reverse}
    {...props}
  >
    {children}
  </StyledRouterLink>
);

export default Link;
